/** @typedef {import('./filterBar').FilterField} FilterField */

import React, { useState, useCallback, useEffect } from 'react';
import debounce from 'lodash/debounce';

import FilterBarInput from './FilterBarInput';
import { TestableButton } from '@components/general/TestableComponents';
import { ENABLE_EVENT_SUMMARY_ADVANCED_FILTERS } from '@home/config';

import PropTypes from 'prop-types';

export { getOptions, getPriorityOptions, buildSelectOptions } from './filterBar';

/**
 * @typedef {Object} FilterField
 * @property {string} key - Field key (e.g. 'status', 'priority')
 * @property {string} label - Human-readable label
 * @property {'text' | 'dropdown' | 'date'} type - Type of input
 * @property {string[] | Array<{value: string, label: string}>} [options] - For dropdown: array of strings OR array of {value, label} objects
 * @property {function} [transform] - Optional function to transform string values before displaying (ignored if options are objects)
 * @property {boolean} [advanced] - If true, this filter will be in the advanced/collapsible section
 */

/**
 * Enhanced FilterBar with support for immediate and apply-on-demand filtering
 * 
 * @param {Object} filters - Current filter values (controlled by parent)
 * @param {function} onChange - State setter function to update filters
 * @param {function} [pageChange] - Function to call to change page
 * @param {FilterField[]} fields - Array of filter field definitions
 * @param {boolean} [showAdvanced] - Whether to show advanced filters section
 * @param {string} [advancedLabel] - Label for advanced filters toggle button
 * @param {boolean} [serverFiltering] - If true, shows Apply/Reset buttons instead of immediate filtering
 * @param {function} [onApply] - Called when Apply button is clicked (only in serverFiltering mode)
 * @param {function} [onReset] - Called when Reset button is clicked (only in serverFiltering mode)
 * 
 * @example 
 * // Client-side filtering (immediate)
 * <FilterBar
 *  filters={filters}
 *  onChange={setFilters}
 *  pageChange={setPage}
 *  fields={filterFields}
 * />
 * 
 * // Server-side filtering (apply mode)
 * <FilterBar
 *  filters={appliedFilters}
 *  onChange={setLocalFilters}
 *  pageChange={setPage}
 *  fields={filterFields}
 *  serverFiltering={true}
 *  onApply={() => setAppliedFilters(localFilters)}
 *  onReset={() => { setLocalFilters({}); setAppliedFilters({}); }}
 * />
 */
export default function FilterBar({ filters, onChange, pageChange, fields, showAdvanced = false, advancedLabel = "Advanced Filters",
    serverFiltering = false, onApply, onReset, ...props}) {

  const [textDisplayValues, setTextDisplayValues] = useState({});
  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(false);
  
  showAdvanced = ENABLE_EVENT_SUMMARY_ADVANCED_FILTERS && showAdvanced; // Enabled/disable advanced filters based on env var and prop combo
  // Local state for server filtering mode
  const [localFilters, setLocalFilters] = useState(filters);

  // Sync local filters when external filters change (for server mode)
  useEffect(() => {
    if (serverFiltering) {
      setLocalFilters(filters);
    }
  }, [filters, serverFiltering]);

  // Split fields into essential and advanced
  const essentialFields = fields.filter(field => !field.advanced);
  const advancedFields = fields.filter(field => field.advanced);

  // Determine which filters to use for display
  const displayFilters = serverFiltering ? localFilters : filters;

  const hasActiveFilters = Object.values(displayFilters).some(value => 
    value !== null && value !== '' && (!Array.isArray(value) || value.length > 0)
  );

  // Check if local filters differ from applied filters (for server mode)
  const hasUnappliedChanges = serverFiltering && JSON.stringify(localFilters) !== JSON.stringify(filters);

  // Debounce filter update to avoid excessive re-renders (only for immediate mode)
  const debouncedOnChange = useCallback(
    debounce((key, value) => {
      if (!serverFiltering) {
        onChange(prev => ({ ...prev, [key]: value }));
        if (pageChange && typeof(pageChange) === 'function') {
          pageChange(1);
        }
      }
    }, 300),
    [onChange, pageChange, serverFiltering]
  );

  const handleFilterChange = useCallback((key, value) => {
    if (value === 'All') { 
      value = ''; 
    }

    if (serverFiltering) {
      // In server mode, update local state immediately but do not call onChange until Apply is clicked
      setLocalFilters(prev => ({ ...prev, [key]: value }));
    } else {
      // In immediate mode, check if value changed and update
      if (filters[key] === value) { return; }
      onChange(prev => ({...prev, [key]: value}));

      if (pageChange && typeof(pageChange) === 'function') { 
        pageChange(1);
      }
    }
  }, [onChange, pageChange, filters, serverFiltering]);

  const handleTextChange = useCallback((key, value) => {
    // Update display immediately
    setTextDisplayValues(prev => ({ ...prev, [key]: value }));
    
    if (serverFiltering) {
      // In server mode, update local state immediately
      setLocalFilters(prev => ({ ...prev, [key]: value }));
    } else {
      // In immediate mode, use debounced update
      debouncedOnChange(key, value);
    }
  }, [debouncedOnChange, serverFiltering]);

  const handleApply = useCallback(() => {
    if (onApply) {
      onApply(localFilters);
    } else {
      onChange(localFilters);
    }
    if (pageChange && typeof(pageChange) === 'function') {
      pageChange(1);
    }
  }, [localFilters, onApply, onChange, pageChange]);

  const handleReset = useCallback(() => {
    const clearedFilters = Object.keys(displayFilters).reduce((acc, key) => {
      acc[key] = '';
      return acc;
    }, {});
    
    setTextDisplayValues({});
    
    if (serverFiltering) {
      setLocalFilters(clearedFilters);
      if (onReset) {
        onReset();
      } else {
        onChange(clearedFilters);
      }
    } else {
      onChange(clearedFilters);
    }
    
    if (pageChange && typeof(pageChange) === 'function') {
      pageChange(1);
    }
  }, [displayFilters, onChange, pageChange, serverFiltering, onReset]);

  return (
    <div className={`w-full bg-gray-100 rounded p-4 mb-4 ${props.className || ''}`}>

      <div className="flex flex-wrap gap-4 mb-4">
        {essentialFields.map(field => (
          <div key={field.key} className="flex-[1_1_200px] min-w-[200px] max-w-[25%] px-2 py-1">
            <label data-testid="filter-label" className="text-sm font-semibold mb-1 block">
              {field.label}
            </label>
            <FilterBarInput
              field={field}
              displayValue={displayFilters[field.key]}
              textDisplayValue={textDisplayValues[field.key]}
              onTextChange={handleTextChange}
              onChange={handleFilterChange}
            />
          </div>
        ))}
      </div>

      {showAdvanced && advancedFields.length > 0 && (
        <>
          {isAdvancedExpanded && (
            <div className="flex flex-wrap gap-4 mb-4 pt-4 border-t border-gray-300">
              {advancedFields.map(field => (
                <div key={field.key} className="flex-[1_1_200px] min-w-[200px] max-w-[25%] px-2 py-1">
                  <label data-testid="filter-label" className="text-sm font-semibold mb-1 block">
                    {field.label}
                  </label>
                  <FilterBarInput
                    field={field}
                    displayValue={displayFilters[field.key]}
                    textDisplayValue={textDisplayValues[field.key]}
                    onTextChange={handleTextChange}
                    onChange={handleFilterChange}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <div className="flex justify-between items-center pt-4 border-t border-b pb-4 border-gray-300">
        <div className="flex gap-2 w-full">
          {showAdvanced && advancedFields.length > 0 && (
            <TestableButton
              dataTestId="filter-advanced-toggle"
              variant="outlined"
              size="sm"
              onClick={() => setIsAdvancedExpanded(!isAdvancedExpanded)}
              className="text-sm"
            >
              {isAdvancedExpanded ? 'Hide' : 'Show'} {advancedLabel}
            </TestableButton>
          )}
          
          {serverFiltering ? ( // Server filtering mode buttons
            <div className="flex ml-auto gap-x-4">
              <TestableButton
                dataTestId="filter-apply-button"
                size="sm"
                onClick={handleApply}
                className="text-sm bg-green-500"
                disabled={!hasUnappliedChanges}
              >
                Apply Filters
              </TestableButton>
              
              {hasActiveFilters && (
                <TestableButton
                  dataTestId="filter-reset-button"
                  variant="outlined"
                  size="sm"
                  onClick={handleReset}
                  className="text-sm"
                >
                  Reset
                </TestableButton>
              )}
            </div>
          ) : ( // Clear button for immediate filtering mode
            hasActiveFilters && (
              <TestableButton
                dataTestId="filter-clear-button"
                variant="outlined"
                size="sm"
                onClick={handleReset}
                className="text-sm"
              >
                Clear All
              </TestableButton>
            )
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 w-full">
        {/* Filter count indicator */}
        <div className="flex items-center gap-2 ml-auto mt-1">
          {hasUnappliedChanges && (
            <span className="text-lg text-orange-800 font-bold">Unsaved changes</span>
          )}
          {hasActiveFilters && (
            <span className="text-lg text-orange-800">
              {Object.values(displayFilters).filter(value => 
                value !== null && value !== '' && (!Array.isArray(value) || value.length > 0)
              ).length} filter(s) active
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

FilterBar.propTypes = {
  filters: PropTypes.object.isRequired,
  onChange: PropTypes.func,
  pageChange: PropTypes.func,
  showAdvanced: PropTypes.bool,     // If true, shows advanced filters section
  advancedLabel: PropTypes.string,  // Label for the advanced filters toggle button
  serverFiltering: PropTypes.bool,  // If true, shows Apply/Reset buttons instead of immediate filtering
  onApply: PropTypes.func,          // Used in server filtering mode to apply local filters
  onReset: PropTypes.func,          // Used in server filtering mode to reset filters  
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['text', 'dropdown', 'date']).isRequired,
      advanced: PropTypes.bool,
      options: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.string),
        PropTypes.arrayOf(PropTypes.shape({
          value: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired
        }))
      ]),
      transform: PropTypes.func
    })
  ).isRequired,
  className: PropTypes.string,
};